/**
 * Call waiting in the browser: a second call during a call, and the call that
 * waits on hold while the person talks to the second one.
 *
 * Three pieces of state beside the provider's one call in front:
 *
 *  - the KNOCK: an offer that arrived while busy and that the person chose to
 *    hear (`busyMode: "knock"`). It is not attached to the call state - the call
 *    in front is still the one being talked to - so it lives here with its own
 *    card and its own short tone.
 *  - the HELD call: the call that was in front when a knock was answered. Its
 *    customer is on SERVER hold (hold music, the same request as the Hold
 *    button), and the browser's leg stays in that room, muted, so the room and
 *    the customer stay up. Only one call is ever held.
 *  - the turn-down: `voicemail` and `queue` refuse the offer at once, and the
 *    server decides what the caller gets.
 *
 * Why the browser's leg must stay: both bridges give the operator's leg
 * `endConferenceOnExit`, so letting the Voice SDK hang it up when the second
 * call is accepted - which it does by default - would end the held customer's
 * call. See `keepOtherCalls.ts`.
 *
 * Every server step that can fail is done BEFORE the screen changes, so a
 * failure leaves the person where they were with a sentence, never with a
 * customer on hold and no way back.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
  type Dispatch, type MutableRefObject, type SetStateAction,
} from "react";
import type { Call } from "@twilio/voice-sdk";
import {
  findParkedOffer,
  getCallBridge,
  lookupPhone,
  setCallHold,
  type ParkedOffer,
  type PhoneMatch,
} from "../../../lib/calls";
import type { CallAudio } from "../audio/apply";
import { IDLE, isLiveCall, type CallPhase, type CallState } from "../callState";
import { claimOffer, routeAnswerAudio } from "../incoming/claimOffer";
import { waitForParkedOffer } from "../parkedOffer";
import { TERMINAL } from "../useCallBridge";
import type { AttachCall } from "../useCallEvents";
import {
  busyVerdict,
  canTakeBack,
  isBusy,
  knockAnswerBlock,
  type AnswerBlock,
  type BusyMode,
} from "./busyMode";
import { keepOtherCallsOnAccept } from "./keepOtherCalls";

/** The call on hold, with everything needed to put it back in front. */
export interface HeldCall {
  /** The browser's leg in the held room. Null when the person is on their mobile. */
  call: Call | null;
  /** The call's state as it was when it went on hold. */
  state: CallState;
}

/** What the knock card draws. */
export interface KnockView {
  from: string;
  matches: PhoneMatch[];
  intentDepartment: string;
  locale: string;
  /** Answer was pressed and the steps are running. */
  pending: boolean;
  /** Why the last Answer did not go through: `hold_failed` or `sdk_blocked`. */
  error: string | null;
}

/** What the held strip draws. */
export interface HeldView {
  /** The held call's row, which the strip's poll asks about. */
  callLogId: string | null;
  /** The best name known for the held customer, "" when there is none. */
  name: string;
  /** The held customer's number, "" when it was withheld. */
  number: string;
  pending: boolean;
  /** `hold_failed` (the call in front could not be held) or `resume_failed`. */
  error: string | null;
}

export interface CallWaitingApi {
  knock: KnockView | null;
  held: HeldView | null;
  /** Whether Answer is offered on the knock card, and why not. */
  answerBlock: AnswerBlock;
  /** Whether the held call can be taken back right now. */
  canTakeBack: boolean;
  answer: () => void;
  decline: () => void;
  takeBack: () => void;
}

interface KnockOffer {
  call: Call;
  callLogId: string;
  from: string;
  legSid: string;
  intentDepartment: string;
  locale: string;
  parked: Promise<ParkedOffer | null> | null;
}

export interface CallWaitingOptions {
  state: CallState;
  setState: Dispatch<SetStateAction<CallState>>;
  phaseRef: MutableRefObject<CallPhase>;
  sessionRef: MutableRefObject<number>;
  callRef: MutableRefObject<Call | null>;
  conferenceRef: MutableRefObject<boolean>;
  /** Owned by the provider, which reads it to keep the device while a call is held. */
  heldRef: MutableRefObject<HeldCall | null>;
  attach: AttachCall;
  callAudio: () => CallAudio | null;
  /** The person's current choice. */
  busyMode: () => BusyMode;
}

/** How often the held row is asked whether its customer is still there. */
const HELD_POLL_MS = 4000;

function heldViewOf(h: HeldCall): HeldView {
  const s = h.state;
  const name = s.incoming?.matches[0]?.name || s.target?.companyName || "";
  return {
    callLogId: s.callLogId,
    name,
    number: s.dialedNumber ?? s.incoming?.from ?? "",
    pending: false,
    error: null,
  };
}

export function useCallWaiting(opts: CallWaitingOptions): {
  api: CallWaitingApi;
  /** A new offer would be a second call: knock or turn it down, not ring. */
  busy: () => boolean;
  /** Handle an offer that arrived while `busy()`. */
  offerWhileBusy: (call: Call) => void;
} {
  const { state, setState, phaseRef, sessionRef, callRef, conferenceRef, heldRef, attach, callAudio, busyMode } = opts;
  const [knock, setKnock] = useState<KnockView | null>(null);
  const [held, setHeld] = useState<HeldView | null>(null);
  const knockRef = useRef<KnockOffer | null>(null);
  const stateRef = useRef(state);
  const workingRef = useRef(false);
  /** The listener that notices a held customer hanging up, per held call. */
  const goneRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const patchKnock = useCallback((patch: Partial<KnockView>) => {
    setKnock((k) => (k ? { ...k, ...patch } : k));
  }, []);
  const patchHeld = useCallback((patch: Partial<HeldView>) => {
    setHeld((h) => (h ? { ...h, ...patch } : h));
  }, []);
  const clearKnock = useCallback(() => {
    knockRef.current = null;
    setKnock(null);
  }, []);

  /** The held customer hung up, or their room ended. Nothing is left to take back. */
  const heldGone = useCallback((h: HeldCall) => {
    if (heldRef.current !== h) return;
    heldRef.current = null;
    goneRef.current = null;
    setHeld(null);
    try {
      h.call?.disconnect();
    } catch {
      // Already gone.
    }
  }, [heldRef]);

  /** Put this call in the held slot. Its customer is already on server hold. */
  const park = useCallback((h: HeldCall) => {
    heldRef.current = h;
    // Muted as well as held. The customer cannot hear the room while on hold,
    // but a leg recording on THIS leg would otherwise record the person's side
    // of the next conversation into the held call's file.
    h.call?.mute(true);
    const onGone = () => heldGone(h);
    goneRef.current = onGone;
    h.call?.once("disconnect", onGone);
    setHeld(heldViewOf(h));
  }, [heldRef, heldGone]);

  /** Bring a held call to the front. Its customer is already OFF hold on the server. */
  const toFront = useCallback((h: HeldCall) => {
    if (goneRef.current) h.call?.removeListener("disconnect", goneRef.current);
    goneRef.current = null;
    heldRef.current = null;
    setHeld(null);
    const session = ++sessionRef.current;
    const live = () => sessionRef.current === session;
    const update = (patch: Partial<CallState>) => {
      if (live()) setState((prev) => ({ ...prev, ...patch }));
    };
    conferenceRef.current = h.state.conference;
    phaseRef.current = "active";
    setState({ ...h.state, phase: "active", hold: false, holdPending: false, error: null });
    if (h.call) {
      attach(h.call, live, update);
      // Back to the person's own mute state from before the hold, not simply
      // unmuted: somebody who had muted themselves stays muted.
      h.call.mute(h.state.muted);
    } else {
      callRef.current = null;
    }
  }, [heldRef, sessionRef, setState, conferenceRef, phaseRef, attach, callRef]);

  // The held customer can hang up while nobody is looking at them. The leg's
  // own `disconnect` covers a browser leg; this covers a call on the person's
  // mobile, and a signalling socket that has stopped telling us anything.
  const heldId = held?.callLogId ?? null;
  useEffect(() => {
    if (!heldId) return;
    let stop = false;
    const tick = async () => {
      try {
        const s = await getCallBridge(heldId);
        const h = heldRef.current;
        if (!stop && h && h.state.callLogId === heldId && TERMINAL.has(s.status)) heldGone(h);
      } catch {
        // A missed poll is a network blip. The next one asks again.
      }
    };
    const timer = window.setInterval(() => void tick(), HELD_POLL_MS);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [heldId, heldRef, heldGone]);

  const busy = useCallback(
    () => isBusy(phaseRef.current, heldRef.current !== null, knockRef.current !== null),
    [phaseRef, heldRef],
  );

  const offerWhileBusy = useCallback((call: Call) => {
    const verdict = busyVerdict({ mode: busyMode(), phase: phaseRef.current, knocking: knockRef.current !== null });
    if (verdict === "turn-down") {
      // At once, so the server hears "busy" and can do what the person chose
      // (voicemail, or the waiting line) instead of a ring nobody answers.
      try {
        call.reject();
      } catch {
        // Already gone.
      }
      return;
    }

    const callLogId = call.customParameters?.get("callLogId") ?? "";
    const from = (call.parameters?.From ?? "").trim();
    const legSid = callLogId ? "" : (call.parameters?.CallSid ?? "");
    const offer: KnockOffer = {
      call,
      callLogId,
      from,
      legSid,
      intentDepartment: call.customParameters?.get("intentDepartment") ?? "",
      locale: call.customParameters?.get("noticeLocale") ?? "",
      parked: null,
    };
    knockRef.current = offer;
    setKnock({
      from,
      matches: [],
      intentDepartment: offer.intentDepartment,
      locale: offer.locale,
      pending: false,
      error: null,
    });
    // The caller gave up, or a colleague answered: the card goes with the offer.
    const gone = () => {
      if (knockRef.current === offer) clearKnock();
    };
    call.on("cancel", gone);
    call.on("disconnect", gone);

    const current = () => knockRef.current === offer;
    const lookup = (number: string) => {
      void lookupPhone(number)
        .then((res) => {
          if (current()) patchKnock({ matches: res.matches });
        })
        .catch(() => {
          // A number and no name is a worse card, not a broken one.
        });
    };
    if (from) lookup(from);
    if (legSid) {
      offer.parked = waitForParkedOffer(legSid, () => current() || callRef.current === call, findParkedOffer).then(
        (found) => {
          if (found && current()) {
            offer.callLogId = found.callLogId;
            offer.intentDepartment = found.intentDepartment;
            offer.locale = found.noticeLocale;
            patchKnock({
              from: found.from || offer.from,
              intentDepartment: found.intentDepartment,
              locale: found.noticeLocale,
            });
            if (found.from && found.from !== from) {
              offer.from = found.from;
              lookup(found.from);
            }
          }
          return found;
        },
      );
    }
  }, [busyMode, phaseRef, callRef, clearKnock, patchKnock]);

  const decline = useCallback(() => {
    const offer = knockRef.current;
    if (!offer || workingRef.current) return;
    clearKnock();
    try {
      offer.call.reject();
    } catch {
      // Already gone.
    }
  }, [clearKnock]);

  const answer = useCallback(() => {
    const offer = knockRef.current;
    if (!offer || workingRef.current) return;
    const front = stateRef.current;
    if (knockAnswerBlock(front, heldRef.current !== null) !== "ok") return;
    const frontLive = isLiveCall(phaseRef.current);
    const frontCall = frontLive ? callRef.current : null;
    // Checked before anything changes: with a browser leg up anywhere, the
    // SDK's own "hang the other call up" step has to come off this offer, and
    // when it cannot, nothing else may happen either.
    if ((frontCall || heldRef.current?.call) && !keepOtherCallsOnAccept(offer.call)) {
      patchKnock({ error: "sdk_blocked" });
      return;
    }
    workingRef.current = true;
    patchKnock({ pending: true, error: null });

    void (async () => {
      let parked: HeldCall | null = null;
      try {
        if (frontLive && front.callLogId) {
          // The same request the Hold button sends. On failure nothing has
          // moved: the first call is still in front and the knock still rings.
          try {
            await setCallHold(front.callLogId, true);
          } catch {
            patchKnock({ pending: false, error: "hold_failed" });
            return;
          }
          // The caller gave up while the hold was on its way. Bring the first
          // customer straight back rather than leave them on music.
          if (knockRef.current !== offer) {
            void setCallHold(front.callLogId, false).catch(() => {});
            return;
          }
          parked = { call: frontCall, state: { ...stateRef.current, hold: true, holdPending: false } };
          park(parked);
        }

        // The new call takes the front, as the incoming card's Answer would.
        clearKnock();
        const session = ++sessionRef.current;
        const live = () => sessionRef.current === session;
        const update = (patch: Partial<CallState>) => {
          if (live()) setState((prev) => ({ ...prev, ...patch }));
        };
        const incoming = {
          callLogId: offer.callLogId,
          from: offer.from,
          matches: [] as PhoneMatch[],
          intentDepartment: offer.intentDepartment,
          locale: offer.locale,
          legSid: offer.legSid,
        };
        // An inbound call is never a conference in the call state; see
        // `CallProvider.handleIncoming`.
        conferenceRef.current = false;
        phaseRef.current = "connecting";
        setState({
          ...IDLE,
          phase: "connecting",
          direction: "inbound",
          incoming,
          dialedNumber: offer.from || null,
          callLogId: offer.callLogId || null,
        });
        attach(offer.call, live, update);
        void lookupPhone(offer.from)
          .then((res) => {
            if (live()) setState((prev) => (prev.incoming ? { ...prev, incoming: { ...prev.incoming, matches: res.matches } } : prev));
          })
          .catch(() => {});
        // Cancelled between Answer and the audio: whatever was held comes back.
        offer.call.on("cancel", () => {
          if (!live()) return;
          callRef.current = null;
          backAfterFailedAnswer();
        });

        const claim = await claimOffer({ callLogId: offer.callLogId, legSid: offer.legSid, parked: offer.parked });
        if (claim.refused) {
          if (callRef.current === offer.call) {
            try {
              offer.call.reject();
            } catch {
              // Already gone.
            }
            callRef.current = null;
          }
          backAfterFailedAnswer();
          return;
        }
        if (claim.callLogId) {
          if (live()) {
            setState((prev) => ({
              ...prev,
              callLogId: claim.callLogId,
              incoming: prev.incoming ? { ...prev.incoming, callLogId: claim.callLogId } : prev.incoming,
              ...(claim.recording !== null ? { recording: claim.recording } : {}),
            }));
          }
        }
        if (callRef.current !== offer.call) return;
        await routeAnswerAudio(callAudio);
        try {
          offer.call.accept();
        } catch {
          update({ phase: "error", error: "connect_failed" });
        }
      } finally {
        workingRef.current = false;
      }
    })();

    /** The new call never came up: put the held one back in front, off hold. */
    function backAfterFailedAnswer() {
      const h = heldRef.current;
      if (!h) {
        sessionRef.current++;
        phaseRef.current = "idle";
        setState(IDLE);
        return;
      }
      toFront(h);
      // The front is this call again either way; a failed release from hold is
      // shown where the Hold button's own failure is.
      if (h.state.callLogId) {
        void setCallHold(h.state.callLogId, false).catch(() =>
          setState((prev) => ({ ...prev, error: "hold_failed" })),
        );
      }
    }
  }, [heldRef, phaseRef, callRef, patchKnock, park, clearKnock, sessionRef, setState, conferenceRef, attach, callAudio, toFront]);

  const takeBack = useCallback(() => {
    const h = heldRef.current;
    if (!h || workingRef.current) return;
    const front = stateRef.current;
    if (!canTakeBack(front)) return;
    const frontLive = isLiveCall(phaseRef.current);
    const frontCall = frontLive ? callRef.current : null;
    workingRef.current = true;
    patchHeld({ pending: true, error: null });

    void (async () => {
      try {
        // A swap holds the call in front first, so the person is never
        // talking to two customers at once.
        if (frontLive && front.callLogId) {
          try {
            await setCallHold(front.callLogId, true);
          } catch {
            patchHeld({ pending: false, error: "hold_failed" });
            return;
          }
        }
        if (h.state.callLogId) {
          try {
            await setCallHold(h.state.callLogId, false);
          } catch {
            if (frontLive && front.callLogId) void setCallHold(front.callLogId, false).catch(() => {});
            patchHeld({ pending: false, error: "resume_failed" });
            return;
          }
        }
        // Both moves landed on the server. Now the screen follows.
        const next: HeldCall | null = frontLive
          ? { call: frontCall, state: { ...stateRef.current, hold: true, holdPending: false } }
          : null;
        toFront(h);
        if (next) park(next);
      } finally {
        workingRef.current = false;
      }
    })();
  }, [heldRef, phaseRef, callRef, patchHeld, toFront, park]);

  const api = useMemo<CallWaitingApi>(
    () => ({
      knock,
      held,
      answerBlock: knockAnswerBlock(state, held !== null),
      canTakeBack: canTakeBack(state),
      answer,
      decline,
      takeBack,
    }),
    [knock, held, state, answer, decline, takeBack],
  );

  return { api, busy, offerWhileBusy };
}
